import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect, useMemo, useState } from "react";
import Landing from "@/pages/Landing";
import PricingPage from "@/pages/PricingPage";
import ProductsPage from "@/pages/ProductsPage";
import PortfolioPage from "@/pages/PortfolioPage";
import TosPage from "@/pages/TosPage";
import RafflePage from "@/pages/RafflePage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import CfxCallback from "@/pages/CfxCallback";
import { CheckoutComplete, CheckoutCancel } from "@/pages/CheckoutStatus";
import { CfxAuthProvider } from "@/context/CfxAuthContext";
import { CartProvider } from "@/context/CartContext";
import { ThemeProvider } from "@/context/ThemeContext";
import CartDrawer from "@/components/CartDrawer";
import CustomCursor from "@/components/CustomCursor";
import AudioPlayer from "@/components/AudioPlayer";
import { fetchConfig } from "@/api";

function App() {
  // Fetch the full site config once at the root. Used for theme AND to feed
  // the top-level AudioPlayer so music persists across route changes (the
  // player lives OUTSIDE <Routes> so it never unmounts when navigating).
  const [config, setConfig] = useState(null);
  useEffect(() => {
    fetchConfig().then(setConfig).catch(() => {});
  }, []);

  // Build the audio queue from settings. Kept identical to the previous
  // Landing.jsx logic so behavior doesn't change — only the mount point does.
  const audioUrls = useMemo(() => {
    const s = config?.settings;
    if (!s) return [];
    const queue = s.youtube_queue || [];
    if (queue.length > 0) return queue;
    return s.youtube_url ? [s.youtube_url] : [];
  }, [config]);

  const setTheme = (nextTheme) => {
    // Called by AdminDashboard when the admin changes the theme.
    setConfig((prev) => (prev ? { ...prev, theme: nextTheme } : prev));
  };

  return (
    <div className="App font-body">
      <ThemeProvider theme={config?.theme || null}>
        <BrowserRouter>
          <CfxAuthProvider>
            <CartProvider>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/shop" element={<ProductsPage />} />
                <Route path="/portfolio" element={<PortfolioPage />} />
                <Route path="/tos" element={<TosPage />} />
                <Route path="/raffle" element={<RafflePage />} />
                <Route path="/admin" element={<AdminLogin />} />
                <Route path="/admin/dashboard" element={<AdminDashboard onThemeChange={setTheme} />} />
                <Route path="/auth/cfx/callback" element={<CfxCallback />} />
                <Route path="/checkout/complete" element={<CheckoutComplete />} />
                <Route path="/checkout/cancel" element={<CheckoutCancel />} />
              </Routes>
              {/* Global overlays — mounted OUTSIDE Routes so they persist across navigation. */}
              <AudioPlayer urls={audioUrls} />
              <CartDrawer />
              <CustomCursor />
            </CartProvider>
          </CfxAuthProvider>
        </BrowserRouter>
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: "var(--theme-surface)",
              border: "1px solid var(--theme-border)",
              color: "var(--theme-text)",
              borderRadius: 12,
              fontFamily: "Manrope, sans-serif",
              fontSize: "13px",
            },
          }}
        />
      </ThemeProvider>
    </div>
  );
}

export default App;
